/* eslint-disable radix */
/* eslint-disable no-param-reassign */
/* eslint-disable no-console */

import { AdUtils, safeParseArr, safeParseJson } from './ad-utils';
import { AdInfo, AdInfoVL, AdPlayerReportItem } from './types/ad';
import { AdReportItem } from './types/ad-report';
import { AdRequest } from './types/ad-request';
import { CGIReportType } from './types/ad-request-enum';

// 解析SSP自建接口返回的广告数据
export const parseAdInfoFromListRepData = (adItem: AdRequest.AdListRepDataAdItem): AdInfo | null => {
  if (!adItem.poster || !adItem.order || !adItem.poster.ad_action) {
    return null;
  }
  // action_type：102 小程序，104 小游戏，wx_type：WxLinkType｛ MINI_PROGRAM = 1, // 小程序  MINI_GAME = 2, // 小游戏 ｝
  const amsLinkParams: Record<string, string> = {
    ...(adItem.order.ams_link_report || {}),
    aid: adItem.order.order_id || '',
    traceid: adItem.order.trace_id || '',
    wx_type: adItem.poster.ad_action.action_type === 102 ? '1' : '2',
  };

  let cgiOriginExposureReport: AdReportItem[] = [];
  let cgiEffectExposureReport: AdReportItem[] = [];
  let thirdClickReport: AdReportItem[] = [];
  let videoReport: AdPlayerReportItem = {
    url: '',
    maxReportCount: 0,
  };
  const reportDict = adItem.order.report_dict;
  if (reportDict) {
    const keys: CGIReportType[] = [
      CGIReportType.REPORT_TYPE_ORIGIN_EXPOSURE,
      CGIReportType.REPORT_TYPE_EFFECT_EXPOSURE,
      CGIReportType.REPORT_TYPE_THIRD_CLICK,
      CGIReportType.REPORT_TYPE_AD_PLAY,
    ];
    for (const key of keys) {
      if (reportDict[key]?.reports) {
        const { reports } = reportDict[key];
        if (key === CGIReportType.REPORT_TYPE_ORIGIN_EXPOSURE) {
          cgiOriginExposureReport = AdUtils.requestInfo2reportItem(reports);
        } else if (key === CGIReportType.REPORT_TYPE_EFFECT_EXPOSURE) {
          cgiEffectExposureReport = AdUtils.requestInfo2reportItem(reports);
        } else if (key === CGIReportType.REPORT_TYPE_THIRD_CLICK) {
          thirdClickReport = AdUtils.requestInfo2reportItem(reports);
        } else if (key === CGIReportType.REPORT_TYPE_AD_PLAY && reports?.length > 0) {
          videoReport = {
            url: reports[0].url,
            maxReportCount: Number(reportDict[key].max_report_cycles || 5),
          };
        }
      }
    }
  }

  let tags: string[] = [];
  let labels: AdRequest.AdLabelItem[] = [];
  if (adItem.poster.ad_label) {
    tags = adItem.poster.ad_label.label_list || [];
    labels = adItem.poster.ad_label.labels || [];
  }

  const adInfo: AdInfo = {
    style: adItem.ad_style,

    orderId: adItem.order.order_id || '',
    traceId: adItem.order.trace_id || '',

    title: adItem.poster.ad_title,
    subTitle: adItem.poster.short_title || '',
    advertiserName: adItem.poster.advertiser?.advertiser_name ?? '',
    coverPic: adItem.poster.image_url || '',
    avatarUrl: adItem.poster.advertiser?.advertiser_url ?? '',
    qrCodeUrl: adItem.poster.qr_code?.short_link ?? '',
    qrCodeTip: adItem.poster.qr_code?.tips ?? '',

    tags,
    labels,

    interactiveModes: adItem.poster.interactive_modes || [],

    actionType: adItem.poster.ad_action.action_type || 0,
    actionBtnText: adItem.poster.action_button.button_text || '',
    closeBtnText: adItem.poster.close_title || '',

    miniProgram: null,
    landingPage: adItem.poster.landing_page || null,
    enableClickScan: adItem.poster.enable_click_scan || false,
    inAppJumpLink:
        adItem.poster.ad_action.action_item?.open_app_action?.open_url ?? '',

    jumpAction: adItem.poster.ad_action,

    clickUrl: adItem.order.click_url || '', // CGI点击上报
    cgiOriginExposureReport, // CGI原始曝光上报
    cgiEffectExposureReport, // CGI有效曝光上报
    thirdClickReport,
    videoReport,

    vid: adItem.poster.vid || '',

    vrReportParams: adItem.order?.vr_report || {},
    amsLinkParams, // CGI点击上报以及后链路上报参数

    shortDrama: adItem.poster.native_short_video,
    pcSDK: adItem.poster.pc_sdk_info,

    isFirstOriginalExposure: true,
    isFirstEffectiveExposure: true,
  };

  if (adInfo.actionType === 102 || adInfo.actionType === 104) {
    adInfo.miniProgram = {
      appid: adItem.poster.ad_action.data?.wxa_app_id ?? '',
      path: adItem.poster.ad_action.data?.open_url ?? '',
    };
  }
  if ('position' in adItem.order) {
    adInfo.position = adItem.order.position;
  }
  return adInfo;
};

// 解析频道页信息流返回的广告数据（老版）
export const parseAdInfoVLFromChannelParams = (
  adParams: Record<string, string>,
  reportInfos: Record<string, any>,
): AdInfoVL | null => {
  if (!adParams?.order_id) {
    return null;
  }
  const adAction = safeParseJson(adParams.ad_action);
  const control = safeParseJson(adParams.control);

  const vrReportDict = reportInfos?.vr_data?.report_dict ?? {};

  let shortDrama: Object = null;
  if ('native_short_video' in adParams && adParams.native_short_video) {
    // 短剧需要取video时间长度
    const duration = parseInt(adParams.duration) || 0;
    shortDrama = Object.assign({}, {
      duration,
    }, safeParseJson(adParams.native_short_video) || {});
  }
  let pcSDK: Object = null;
  if ('pc_sdk_info' in adParams && adParams.pc_sdk_info) {
    pcSDK = Object.assign({}, safeParseJson(adParams.pc_sdk_info) || {});
  }
  let interactiveModes = safeParseArr(adParams.interactive_modes) || [];
  if (!Array.isArray(interactiveModes)) {
    interactiveModes = [interactiveModes];
  }
  const actionBtn = {
    btnText: '',
    duration1: 0,
    duration2: 0,
    style: 0,
  };
  if (adParams.action_button) {
    const actionBtnData = safeParseJson(adParams.action_button) || {};
    actionBtn.duration1 = parseInt(actionBtnData.duration1) || 0;
    actionBtn.duration2 = parseInt(actionBtnData.duration2) || 0;
    actionBtn.btnText = actionBtnData.button_text || '';
  }
  let adLabels = [];
  let labels: AdRequest.AdLabelItem[] = [];
  if (adParams.ad_label) {
    const adLabelsData = safeParseJson(adParams.ad_label) || {};
    if (adLabelsData.label_list) {
      adLabels = adLabelsData.label_list || [];
    }
    if (adLabelsData.labels) {
      labels = adLabelsData.labels || [];
    }
  }

  const amsLinkParams: Record<string, string> = {
    ...(reportInfos?.ams_data?.report_dict || {}),
    aid: adParams.order_id || '',
    traceid: adParams.trace_id || '',
    wx_type: adAction.action_type === 104 ? 2 : 1,
  };

  const orginExposure = safeParseArr(adParams.exposure_urls);
  const effectExposure = safeParseArr(adParams.effective_exposures);
  const thirdClick = safeParseArr(adParams.third_click_urls);

  let qrCodeUrl = adParams.qrcode_url || '';
  if (qrCodeUrl) {
    qrCodeUrl += `${qrCodeUrl.indexOf('?') === -1 ? '?' : '&'}act=1025`;
  }

  const adInfo: AdInfoVL = {
    style: parseInt(adParams.ad_style),

    orderId: adParams.order_id || '',

    title: adParams.ad_title,
    subTitle: adParams.short_title || '', // description
    advertiserName: adParams.advertiser_name ?? '',
    coverPic: adParams.image_url || '',
    avatarUrl: adParams.advertiser_url ?? '',
    qrCodeUrl,
    qrCodeTip: adParams.qrcode_tips,
    tipTitle: adParams.tip_title,

    scene: adParams.ad_scene || '', // 12为网大场景

    interactiveModes,

    tags: adLabels,
    labels,
    autoPlayStyleAni: adParams.auto_play_animation === '1',

    actionType: adAction?.action_type,
    actionBtnText: adParams.btn_title || '',
    closeBtnText: adParams.close_title || '',

    miniProgram: null,
    landingPage: {
      show_type: parseInt(adParams.landing_page_show_type) || 0,
      terminal_popup_url: adParams.landing_page_popup_url,
      right_page_width: parseInt(adParams.right_page_width) || 0,
    },
    enableClickScan: adParams.enable_click_scan === '1',
    inAppJumpLink: adParams.in_app_jump_link || '',

    jumpAction: adAction as AdRequest.AdAction,

    clickUrl: adParams.click_url || '', // CGI点击上报
    cgiOriginExposureReport: AdUtils.record2reportItem(orginExposure),
    cgiEffectExposureReport: AdUtils.record2reportItem(effectExposure), // CGI有效曝光上报
    thirdClickReport: AdUtils.record2reportItem(thirdClick),
    videoReport: {
      url: adParams.video_report_url || '',
      maxReportCount: parseInt(adParams.max_report_cycles || '5') || 0,
    },

    vid: adParams.vid || '',
    cid: adParams.cid || '',
    duration: parseInt(adParams.duration) || 0,
    isAutoPlay: !!adParams.is_auto_play,

    vrReportParams: vrReportDict,
    amsLinkParams, // CGI点击上报以及后链路上报参数

    shortDrama: shortDrama as AdRequest.NativeShortVideoInfo,
    pcSDK: pcSDK as AdRequest.PCSdkInfo,

    isFirstOriginalExposure: true,
    isFirstEffectiveExposure: true,

    actionBtn,
    videoImgTag: adParams.img_tag || '',
    videoTopic: adParams.topic_name || '',
    videoActors: safeParseJson(adParams.leading_actor) || [],

    control,
  };

  if (adInfo.actionType === 102 || adInfo.actionType === 104) {
    if (import.meta.env.EVO) {
      adInfo.miniProgram = {
        appid: adAction.data?.wxa_app_id,
        path: adAction.data?.open_url,
      };
    } else {
      const actionItem = adAction?.action_item;
      const path = actionItem?.open_mini_game?.open_url || actionItem?.open_mini_program?.open_url;
      adInfo.miniProgram = {
        appid: actionItem?.open_mini_game?.wxa_app_id ?? '',
        path,
      };
    }
  }
  if ('position' in adParams) {
    adInfo.position = parseInt(adParams.position);
  }
  return adInfo;
};

// 解析频道页返回的广告JSON数据（新版，网大）
export const parseAdInfoVLFromChannelAdDataItem = (adItem: any): AdInfoVL | null => {
  if (!adItem?.poster || !adItem?.order) {
    return null;
  }
  const adInfo = parseAdInfoFromListRepData(adItem);
  // 新版结构内不会有actionBtn数据，默认为空
  const actionBtn = {
    btnText: '',
    duration1: 0,
    duration2: 0,
    style: 0,
  };
  return {
    ...adInfo,
    actionBtn,
    videoImgTag: adItem.poster.video?.img_tag ?? '',
    videoTopic: adItem.poster.video?.img_tag ?? '',
    videoActors: adItem.poster.video?.actors ?? [],
  };
};
